import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { postContractItem } from "../API/billingAPI";
import billingValidationSchema from "../../components/Billing/billingValidationSchema";
import { formatDateInPhilippines, toStartOfPhilippineDay, todayInPhilippines } from "./utils/phDate";
import { 
  Box, 
  Button, 
  TextField, 
  Grid, 
  Typography, 
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  InputLabel,
  FormHelperText,
  CardHeader,
  CircularProgress,
} from "@mui/material";

const toStartOfDay = (value) => {
  return toStartOfPhilippineDay(value);
}

const deriveContractStatus = (startDate, endDate) => {
  if (!startDate) return 'INACTIVE';

  const today = toStartOfDay(todayInPhilippines());
  const start = toStartOfDay(startDate);
  const end = endDate ? toStartOfDay(endDate) : null;

  if (start > today) return 'INACTIVE';
  if (end && today > end) return 'EXPIRED';
  return 'ACTIVE';
};

const PAYMENT_INTERVAL_MAP = {
  ONE_TIME: null,
  WEEKLY: null,
  MONTHLY: 1,
  QUARTERLY: 3,
  "SEMI-ANNUAL": 6,
  ANNUAL: 12,
  CUSTOM: null,
};

function CreateContract() {
  const CURRENCY = 'PHP';
  const navigate = useNavigate();
  const location = useLocation();
  const selectedClient = location.state?.selectedClient;
  const source = location.state?.source || "manageContracts";

  const handleCancelClick = () => {
    if (source === "manageClients") {
      navigate("/manage-clients", { state: {selectedClient } });
    } else {
      navigate("/manage-contracts", { state: { selectedClient } });
    }
  };

  // for creating contract payload
  const createEmptyContract = () => ({
    contract_id: "",
    client_id: selectedClient?.id || "",
    client_name: selectedClient?.name || "",
    price_minor: 0,
    currency: "PHP",
    payment_terms: {
      type: "MONTHLY",
      interval_months: 1,
      count: 1,
      notes: "",
    },
    contract_start: null,
    contract_end: null,
    status: "ACTIVE"
  })

  const [ errors, setErrors ] = useState({});
  const [ loading, setLoading ] = useState(false);
  const [ successMessage, setSuccessMessage ] = useState("");
  const [ form, setForm ] = useState(createEmptyContract());

  useEffect(() => {
    const autoStatus = deriveContractStatus(form.contract_start, form.contract_end);
    setForm((prev) => 
      prev.status === autoStatus ? prev : { ...prev, status: autoStatus }
    );
  }, [form.contract_start, form.contract_end]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await billingValidationSchema.validate(form, {abortEarly: false});
      setLoading(true);

      const stored = localStorage.getItem('user');
      const storedUser = stored ? JSON.parse(stored) : null;
      const userInfo = storedUser ? {
        user_id: storedUser.username || storedUser.user_id || '',
        name: `${storedUser.firstname || ''} ${storedUser.lastname || ''}`.trim()
      } 
      : { user_id: 'system_default', name: 'system_default' };

      const contractStart = formatDateInPhilippines(form.contract_start);
      const contractEnd = formatDateInPhilippines(form.contract_end);

      const submitData = {
        contract_id: form.contract_id,
        client_id: form.client_id,
        client_name: form.client_name,
        contract_start: contractStart,
        contract_end: contractEnd,
        price_minor: parseInt(form.price_minor) || 0,
        currency: form.currency || 'PHP',
        status: form.status || 'ACTIVE',
        payment_terms: form.payment_terms || {
          type: 'MONTHLY',
          interval_months: 1,
          count: 1,
          notes: '',
        },
        user_id: userInfo.user_id,
        user_name: userInfo.name
      };
      
      // function for calling post API
      const res = await postContractItem(form.client_id, submitData);

      if (!res?.success) {
        setErrors({
          form: res?.error?.error || res?.error?.message || "Failed to create contract",
        });
        return;
      }

      setSuccessMessage("Contract created successfully!");
      setTimeout(() => {
        if (source === "manageClients") {
          navigate("/manage-clients", { state: { selectedClient } });
        } else {
          navigate("/manage-contracts", { state: { selectedClient } });
        }
      }, 1500);      
    } catch (err) {
      if (err.inner) {
        const map = {};
        err.inner.forEach((e) => {
          map[e.path] = e.message;
        });
        setErrors(map);
      } else {
        setErrors({ form: err.message || "Validation error" });
      }
    } finally {
      setLoading(false);
    }
  }

  const handleFieldChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null});
    }
  }

  const handlePaymentTermsChange = (field, value) => {
    setForm((prev) => {
      const paymentTerms = {
        ...prev.payment_terms,
        [field]:
          field === "count"
            ? Number(value)
            : value,
      };

      if (field === "type") {
        paymentTerms.interval_months = PAYMENT_INTERVAL_MAP[value];
      }

      return {
        ...prev,
        payment_terms: paymentTerms,
      };
    });

    setErrors((prev) => ({
      ...prev,
      payment_terms: null,
      form: null,
    }));
  };

  const handlePriceInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    handleFieldChange('price_minor', parseInt(digits || '0', 10));
  };

  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Billing"
      subTitle="Create Contract"
      subTitleRoute={"/create-billing"}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{p:2}}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Contract Details
                </Typography>
              }
              sx= {{ pb: 0, pt: 0 }}
            />
          </Grid>
          
          {/* Client ID  (Read-only) */}
          <Grid item xs={12}>
            <TextField
              label="Client ID"
              value={form.client_id}
              fullWidth
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Client Name"
              value={form.client_name}
              fullWidth
              disabled
              variant="outlined"
            />
          </Grid>
          
          {/* Contract ID */}
          <Grid item xs={12}>
            <TextField
              label="Contract ID"
              value={form.contract_id}
              onChange={(e) => handleFieldChange('contract_id', e.target.value)}
              fullWidth
              error={!!errors.contract_id}
              helperText={errors.contract_id}
              placeholder="Enter contract ID"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {/* Price (minor) */}
              <TextField 
                label='Price'
                type='text'
                value={(form.price_minor / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                onChange={handlePriceInput}
                sx={{ flex: 1 }}
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>{CURRENCY}</InputAdornment>
                }}
                error={!!errors.price_minor}
                helperText={errors.price_minor}
              />
            </Box>
              
          </Grid>

          {/* Contract Start Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker 
                label="Contract Start"
                value={form.contract_start}
                onChange={(date) => handleFieldChange("contract_start", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.contract_start,
                    helperText: errors.contract_start
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          {/* Contract End Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker 
                label="Contract End"
                value={form.contract_end}
                onChange={(date) => handleFieldChange("contract_end", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.contract_end,
                    helperText: errors.contract_end
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Status"
              value={form.status}
              fullWidth
              disabled
              variant="outlined"
              helperText="Automatically derive from contract start/end dates"
            />
          </Grid>
          {/* Payment Terms Section */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography 
                  variant='h6' 
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Payment Terms
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>

          {/* Payment Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.payment_terms.type}
                onChange={(e) => handlePaymentTermsChange("type", e.target.value)}
                label="Type"
              >
                <MenuItem value='ONE_TIME'>One-time payment</MenuItem>
                <MenuItem value='WEEKLY'>Weekly</MenuItem>
                <MenuItem value='MONTHLY'>Monthly</MenuItem>
                <MenuItem value='QUARTERLY'>Quarterly</MenuItem>
                <MenuItem value='SEMI-ANNUAL'>Semi-Annual</MenuItem>
                <MenuItem value='ANNUAL'>Annual</MenuItem>
                <MenuItem value='CUSTOM'>CUSTOM</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Count */}
          <Grid item xs={12} sm={6}>
            <TextField 
              label="count (number of payments)"
              type="number"
              value={form.payment_terms.count}
              onChange={(e) => handlePaymentTermsChange("count", e.target.value)}
              fullWidth
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Notes"
              value={form.payment_terms.notes}
              onChange={(e) => handlePaymentTermsChange("notes", e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>

          {/* Submit and Cancel Buttons */}
          {errors.form && (
            <Grid item xs={12}>
              <FormHelperText error>{errors.form}</FormHelperText>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button 
              variant="contained" 
              type="submit" 
              disabled={loading}
            >
              {loading && <CircularProgress size={20} />}
              {loading ? "Creating..." : "Create Contract"}
            </Button>
            <Button 
              sx={{ml:2}} 
              onClick={handleCancelClick}
              disabled={loading}
            >
              Cancel
            </Button>
          </Grid>
        </Grid> 
      </Box>
    </BaseWrapper>
  ); 
}

export default CreateContract;
